// Updated Navbar component (src/components/common/Navbar.tsx)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BellIcon, 
  UserCircleIcon, 
  ArrowRightOnRectangleIcon,
  MusicalNoteIcon
} from '@heroicons/react/24/outline';
import { 
  isAuthenticated, 
  getCurrentUserName, 
  getCurrentUserId,
  logout,
  getCurrentArtistInfo,
  hasLinkedArtist
} from '../../services/authService';

const Navbar: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [currentArtist, setCurrentArtist] = useState<string | null>(null);
  const [hasArtist, setHasArtist] = useState<boolean>(false);
  const navigate = useNavigate();

  // Check authentication status when mounted and on auth changes
  useEffect(() => {
    const checkAuth = () => {
      setIsLoggedIn(isAuthenticated());
      setUserName(getCurrentUserName());
    };

    checkAuth();
    
    // Listen for auth change events
    window.addEventListener('auth-change', checkAuth);
    
    return () => {
      window.removeEventListener('auth-change', checkAuth);
    };
  }, []);

  // Fetch artist information separately
  useEffect(() => {
    const fetchArtistInfo = async () => {
      if (isLoggedIn) {
        const artistInfo = await getCurrentArtistInfo();
        setCurrentArtist(artistInfo?.name || null);
        setHasArtist(await hasLinkedArtist());
      } else {
        setCurrentArtist(null);
        setHasArtist(false);
      }
    };

    fetchArtistInfo();
    
    // Also check when auth changes
    window.addEventListener('auth-change', fetchArtistInfo);
    
    return () => {
      window.removeEventListener('auth-change', fetchArtistInfo);
    };
  }, [isLoggedIn]);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    setIsLoggedIn(false);
    setUserName(null);
    setCurrentArtist(null);
    setHasArtist(false);
    
    // Notify other components about logout
    window.dispatchEvent(new Event('auth-change'));
    
    // Navigate to home page
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-xl font-bold text-green-600">
                Spotify Artist Tracker
              </Link>
            </div>
            
            {/* Display current artist if available */}
            {isLoggedIn && currentArtist && (
              <div className="ml-6 flex items-center">
                <div className="flex items-center px-3 py-1 bg-green-50 rounded-full">
                  <MusicalNoteIcon className="h-4 w-4 text-green-600 mr-1" />
                  <span className="text-sm font-medium text-green-700">
                    {currentArtist}
                  </span>
                </div>
              </div>
            )}
            
            {/* Prompt to add artist if logged in but no artist linked */}
            {isLoggedIn && !hasArtist && (
              <div className="ml-6 flex items-center">
                <Link to="/profile" className="text-sm text-gray-500 hover:text-green-600">
                  Link your artist profile
                </Link>
              </div>
            )}
          </div>
          
          <div className="flex items-center">
            <button className="p-1 rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 mr-3">
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            
            <div className="ml-3 relative">
              <div>
                <button
                  className="max-w-xs flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span className="sr-only">Open user menu</span>
                  <UserCircleIcon className="h-8 w-8 text-gray-500" aria-hidden="true" />
                  {isLoggedIn && userName && (
                    <span className="ml-2 text-sm font-medium text-gray-700">{userName}</span>
                  )}
                </button>
              </div>
              
              {dropdownOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5">
                  {isLoggedIn ? (
                    <>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Your Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Settings
                      </Link>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={handleLogout}
                      >
                        <div className="flex items-center">
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                          Sign Out
                        </div>
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      state={{ returnPath: window.location.pathname }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Sign in with Spotify
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;